import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, CircleAlert, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover/index.js";
import { cn } from "../../../../lib/utils.js";
import { TEMPLATE_LIMITS } from "../../template-editor-presentation.js";
import {
  COUNTRY_DIAL_CODES,
  COUNTRY_NAMES_HE,
  type CountryDialCode,
} from "./country-dial-codes.js";

export interface WizardPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  touched?: boolean;
}

function splitPhoneValue(value: string): { dialCode: string; localNumber: string } {
  if (!value.startsWith("+")) return { dialCode: "+1", localNumber: value };
  const plusMatches = COUNTRY_DIAL_CODES.filter((country) =>
    value.startsWith(country.dialCode),
  ).toSorted((a, b) => b.dialCode.length - a.dialCode.length);
  const match =
    plusMatches.find((country) => country.code === "US") ?? plusMatches[0];
  if (!match) return { dialCode: "+1", localNumber: value.slice(1) };
  return {
    dialCode: match.dialCode,
    localNumber: value.slice(match.dialCode.length),
  };
}

export function WizardPhoneInput({
  value,
  onChange,
  invalid = false,
  touched = false,
}: WizardPhoneInputProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isHebrewUi = i18n.language === "he";
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [search, setSearch] = useState("");
  const numberRef = useRef<HTMLInputElement>(null);
  const { dialCode, localNumber } = useMemo(() => splitPhoneValue(value), [value]);
  const selectedCountry = COUNTRY_DIAL_CODES.find((country) => country.dialCode === dialCode);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter((country) => {
      const hebrewName = COUNTRY_NAMES_HE[country.code] ?? "";
      return (
        country.name.toLowerCase().includes(query) ||
        hebrewName.toLowerCase().includes(query) ||
        country.dialCode.toLowerCase().includes(query) ||
        country.dialCode.replace("+", "").includes(query) ||
        country.code.toLowerCase().includes(query)
      );
    });
  }, [search]);

  function selectCountry(country: CountryDialCode): void {
    setPrefixOpen(false);
    setSearch("");
    onChange(`${country.dialCode}${localNumber}`);
    requestAnimationFrame(() => numberRef.current?.focus());
  }

  const showError = touched && invalid;

  return (
    <div className="grid gap-2 sm:grid-cols-[minmax(8.5rem,11rem)_minmax(0,1fr)]">
      <label className="grid gap-1 text-xs font-medium text-ink">
        {t("whatsapp.templates.manage.wizard.editor.buttonPhoneCountry")}
        <Popover
          open={prefixOpen}
          onOpenChange={(open) => {
            setPrefixOpen(open);
            if (!open) setSearch("");
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex h-10 w-full items-center justify-between gap-1 rounded-md border bg-surface px-2.5 text-start text-sm tabular-nums [direction:ltr]",
                showError ? "border-error" : "border-line",
              )}
            >
              <span className="min-w-0 truncate">
                {selectedCountry
                  ? `${selectedCountry.code} ${selectedCountry.dialCode}`
                  : dialCode}
              </span>
              <ChevronDown size={14} className="shrink-0 opacity-60" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(20rem,calc(100vw-2rem))] overflow-hidden p-0">
            <div className="flex items-center gap-2 border-b border-line px-2.5 py-2">
              <Search size={14} className="shrink-0 text-ink-muted" aria-hidden="true" />
              <input
                className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("whatsapp.templates.manage.wizard.editor.buttonPhoneSearch")}
              />
            </div>
            <div
              className="max-h-[17.5rem] overflow-y-auto overscroll-contain p-1"
              onWheel={(event) => event.stopPropagation()}
            >
              {filtered.map((country) => {
                const isSelected = country.code === selectedCountry?.code;
                const hebrewName = isHebrewUi ? COUNTRY_NAMES_HE[country.code] : undefined;
                return (
                  <button
                    key={country.code}
                    type="button"
                    className="flex w-full min-w-0 items-center gap-2.5 overflow-hidden rounded-md px-2 py-1.5 text-start hover:bg-surface-muted"
                    onClick={() => selectCountry(country)}
                  >
                    <span
                      className={cn(
                        "size-4 shrink-0 rounded-full border",
                        isSelected ? "border-[4.5px] border-accent" : "border-line-strong",
                      )}
                      aria-hidden="true"
                    />
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span className="truncate text-sm text-ink">{country.name}</span>
                      {hebrewName ? (
                        <span className="truncate text-[0.7rem] leading-tight text-ink-muted">
                          {hebrewName}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-sm tabular-nums text-ink-muted [direction:ltr]">
                      {country.dialCode}
                    </span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </label>

      <label className="grid gap-1 text-xs font-medium text-ink">
        {t("whatsapp.templates.manage.wizard.editor.buttonPhoneNumber")}
        <div className="relative">
          <input
            ref={numberRef}
            dir="ltr"
            inputMode="tel"
            className={cn(
              "h-10 w-full rounded-md border bg-surface py-2 text-sm tabular-nums [unicode-bidi:isolate]",
              isHebrewUi ? "pl-24 pr-3 text-right" : "pr-24 pl-3 text-left",
              showError ? "border-error" : "border-line",
            )}
            value={localNumber}
            maxLength={TEMPLATE_LIMITS.buttonPhone}
            onChange={(event) => {
              const cleaned = event.target.value.replace(/[^0-9]/g, "");
              onChange(`${dialCode}${cleaned}`);
            }}
          />
          <span
            className={cn(
              "pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center gap-1 text-[0.7rem] text-ink-muted",
              isHebrewUi ? "left-3" : "right-3",
            )}
          >
            <span>
              {localNumber.length}/{TEMPLATE_LIMITS.buttonPhone}
            </span>
            {showError ? <CircleAlert size={14} className="text-error" /> : null}
          </span>
        </div>
        {showError ? (
          <span className="text-xs text-error">
            {t("whatsapp.templates.manage.wizard.editor.buttonPhoneError")}
          </span>
        ) : null}
      </label>
    </div>
  );
}
